import React, { useState, useEffect } from 'react'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'
// import { Button } from '@progress/kendo-react-buttons'
import { Checkbox } from '@progress/kendo-react-inputs'
import { useAppDispatch } from 'app/hooks'
import { useLazyGetSystemQuery, useUpdateSystemSettingsMutation } from 'app/services/system.service'
import { systemActions } from 'app/slices/system.slice'
import { BUTTONS, EXPLANATIONS, FORM, TITLES } from 'locales/locales'
import Loading from 'components/spinner/Loading'
import { FixMeLater } from 'types'
import CustomButton from 'components/button/CustomButton'

interface SystemSettings {
  features: {
    fcmDetailEnabled: boolean
    fcmUserDetailEnabled: boolean
    captchaEnabled: boolean
    passwordChangeEnabled: boolean
    passwordResetEnabled: boolean
    groupWithMembers: boolean
    registerEnabled: boolean
    isGroupMembersEnabled: boolean
    otrEnabled: boolean
    mfaEnabled: boolean
    notificationSound: boolean
    showUnpermittedFeatures: boolean
    isShowAllTeamsForConference: boolean
    isPrivacyTextEnabled: boolean
    isGroupMembersVideoCallEnabled: boolean
  }
}

interface FeatureCheckboxProps {
  title: string
  featureKey: keyof SystemSettings['features']
  label: string
  explanation: string
}

const MobileWebSettings: React.FC = () => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const [updateSystem] = useUpdateSystemSettingsMutation()
  const [getSystemSettings] = useLazyGetSystemQuery()

  const [systemSettingId, setSystemSettingId] = useState('')
  const [systemFeatures, setSystemFeatures] = useState<SystemSettings['features']>({
    fcmDetailEnabled: false,
    fcmUserDetailEnabled: false,
    captchaEnabled: false,
    isGroupMembersEnabled: true,
    groupWithMembers: true,
    registerEnabled: false,
    passwordChangeEnabled: false,
    passwordResetEnabled: false,
    otrEnabled: false,
    mfaEnabled: false,
    notificationSound: true,
    showUnpermittedFeatures: false,
    isShowAllTeamsForConference: false,
    isPrivacyTextEnabled: false,
    isGroupMembersVideoCallEnabled: true,
  })

  useEffect(() => {
    fetchSystemSettings()
  }, [])

  const fetchSystemSettings = async () => {
    try {
      Loading.ref.show()
      const response = await getSystemSettings({})
      if (response?.data?.data && Object.keys(response.data.data).length !== 0) {
        setSystemFeatures(response.data.data.features)
        setSystemSettingId(response.data.data._id)
        dispatch(systemActions.updateSystem(response.data.data))
      }
    } catch (error) {
      console.error('Error fetching system settings:', error)
    } finally {
      Loading.ref.hide()
    }
  }

  const handleFeatureToggle = (featureKey: keyof SystemSettings['features']) => {
    setSystemFeatures((prevFeatures) => ({
      ...prevFeatures,
      [featureKey]: !(prevFeatures?.[featureKey] ?? false),
    }))
  }

  const FeatureCheckbox: React.FC<FeatureCheckboxProps> = ({
    title,
    featureKey,
    label,
    explanation,
  }) => (
    <StyledFeatureItem>
      <StyledCheckbox
        checked={systemFeatures?.[featureKey] ?? false}
        onChange={() => handleFeatureToggle(featureKey)}
      />
      <StyledSpecName title={explanation}>{title}</StyledSpecName>
      <FeatureLabel>{label}</FeatureLabel>
    </StyledFeatureItem>
  )

  const handleSubmit = async () => {
    try {
      Loading.ref.show()

      if (systemSettingId) {
        const system: FixMeLater = await updateSystem({
          features: systemFeatures,
          _id: systemSettingId,
        })

        if (system?.data?.success) {
          dispatch(systemActions.updateSystemFeatures(systemFeatures))
        }
      }
    } catch (error) {
      console.error('Error updating system settings:', error)
    } finally {
      Loading.ref.hide()
    }
  }

  return (
    <ContentCard>
      <ContentCardHeader>{t(TITLES.MOBILE_WEB_SETTINGS_TITLE)}</ContentCardHeader>
      <ContentCardBody>
        <TwoColumnContainer>
          {/* Mobile/Web Column */}
          <ColumnContainer>
            <ColumnHeader>{t(TITLES.MOBILE_WEB_SETTINGS_MOBILE_WEB_TITLE)}</ColumnHeader>
            <StyledFeatureContainer>
              <FeatureCheckbox
                title={t(FORM.KVKK)}
                explanation={t(EXPLANATIONS.SYSTEM_SETTINGS_KVKK_CHECKBOX)}
                featureKey="isPrivacyTextEnabled"
                label="Mobile/Web"
              />
              <FeatureCheckbox
                title={t(FORM.FCM_DETAIL)}
                explanation={t(EXPLANATIONS.SYSTEM_SETTINGS_NOTIFICATION_DETAILS_CHECKBOX)}
                featureKey="fcmDetailEnabled"
                label="Mobile/Web"
              />
              <FeatureCheckbox
                title={t(FORM.FCM_USER_DETAIL)}
                explanation={t(EXPLANATIONS.SYSTEM_SETTINGS_NOTIFICATION_USER_DETAILS_CHECKBOX)}
                featureKey="fcmUserDetailEnabled"
                label="Mobile/Web"
              />
              <FeatureCheckbox
                title={t(FORM.MFA_ENABLED)}
                explanation={t(EXPLANATIONS.SYSTEM_SETTINGS_ENABLE_MFA_CHECKBOX)}
                featureKey="mfaEnabled"
                label="Mobile/Web"
              />
              <FeatureCheckbox
                title={t(FORM.CAPTCHA)}
                explanation={t(EXPLANATIONS.SYSTEM_SETTINGS_CAPTCHA_CHECKBOX)}
                featureKey="captchaEnabled"
                label="Mobile/Web"
              />
              <FeatureCheckbox
                title={t(FORM.PASSWORD_CHANGE)}
                explanation={t(EXPLANATIONS.SYSTEM_SETTINGS_CHANGE_PASSWORD_CHECKBOX)}
                featureKey="passwordChangeEnabled"
                label="Mobile/Web"
              />
              <FeatureCheckbox
                title={t(FORM.FORGOT_PASSWORD)}
                explanation={t(EXPLANATIONS.SYSTEM_SETTINGS_FORGOT_PASSWORD_CHECKBOX)}
                featureKey="passwordResetEnabled"
                label="Mobile/Web"
              />
              <FeatureCheckbox
                title={t(FORM.OTR)}
                explanation={t(EXPLANATIONS.SYSTEM_SETTINGS_OTR_CHECKBOX)}
                featureKey="otrEnabled"
                label="Mobile/Web"
              />
              <FeatureCheckbox
                title={t(FORM.SHOW_UNPERMITTED_FEATURES)}
                explanation={t(EXPLANATIONS.SYSTEM_SETTINGS_UNPERMITTED_FEATURES_CHECKBOX)}
                featureKey="showUnpermittedFeatures"
                label="Mobile/Web"
              />
            </StyledFeatureContainer>
          </ColumnContainer>

          {/* Web Column */}
          <ColumnContainer>
            <ColumnHeader>{t(TITLES.MOBILE_WEB_SETTINGS_WEB)}</ColumnHeader>
            <StyledFeatureContainer>
              <FeatureCheckbox
                title={t(FORM.NOTIFICATION_SOUND)}
                explanation={t(EXPLANATIONS.SYSTEM_SETTINGS_NOTIFICATION_SOUND_CHECKBOX)}
                featureKey="notificationSound"
                label="Web"
              />
              <FeatureCheckbox
                title={t(FORM.SHOW_ALL_TEAMS_FOR_CREATE_MEET)}
                explanation={t(EXPLANATIONS.SYSTEM_SETTINGS_SHOW_ALL_TEAMS_FOR_VIDEOCALL_CHECKBOX)}
                featureKey="isShowAllTeamsForConference"
                label="Web"
              />
                 <FeatureCheckbox
                      title={t(FORM.GROUP_MEMBERS_VIDEO_CALL_ENABLE)}
                      explanation={t(EXPLANATIONS.SYSTEM_SETTINGS_USER_GROUPS_VIDEO_CALL_BUTTON_CHECKBOX)}
                      featureKey="isGroupMembersVideoCallEnabled"
                      label="Web"
                    />
              <FeatureCheckbox
                title={t(FORM.GROUP_MEMBERS_ENABLE)}
                explanation={t(EXPLANATIONS.SYSTEM_SETTINGS_USER_GROUPS_BUTTON_CHECKBOX)}
                featureKey="isGroupMembersEnabled"
                label="Web"
              />
              <FeatureCheckbox
                title={t(FORM.GROUP_WITH_MEMBERS)}
                explanation={t(EXPLANATIONS.SYSTEM_SETTINGS_SHOW_MEMBERSHIP_CHECKBOX)}
                featureKey="groupWithMembers"
                label="Web"
              />
            </StyledFeatureContainer>
          </ColumnContainer>

        </TwoColumnContainer>
      </ContentCardBody>
      <FixedButtonContainer>
        <CustomStyledButton text={t(BUTTONS.SAVE)} onClick={handleSubmit} />
      </FixedButtonContainer>
    </ContentCard>
  )
}

export default MobileWebSettings

const ContentCard = styled.div`
  background-color: white;
  border-radius: 4px;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.2), 0 0 6px rgba(0, 0, 0, 0.1);
  margin-bottom: 20px;
  overflow: hidden;
  border: 1px solid #e3ebf6;
  position: relative;
`

const ContentCardHeader = styled.div`
  padding: 16px 20px;
  font-size: 16px;
  font-weight: 500;
  border-bottom: 1px solid #e3ebf6;
`

const ContentCardBody = styled.div`
  padding: 20px;
`

const StyledFeatureItem = styled.label`
  display: flex;
  background: rgb(248 248 248);
  padding: 7px 10px;
  -webkit-box-align: center;
  align-items: center;
  width: 100%;
`

const FeatureLabel = styled.span`
  border-radius: 5px;
  margin-left: 10px;
  font-size: 11px;
  color: white;
  font-weight: bold;
  background: #119fdb;
  padding: 5px 12px;
`

const StyledSpecName = styled.span`
  cursor: pointer;
  flex: 1 1 0%;
  text-align: left;
  padding-right: 13px;
  padding-left: 14px;
  font-weight: 500;
  margin-top: -3px;
`

const StyledFeatureContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 10px;
`

const StyledCheckbox = styled(Checkbox)`
  .k-checkbox {
    width: 18px;
    height: 18px;
    border: 2px solid #3151b7;
    border-radius: 3px;
  }

  .k-checkbox:checked {
    background-color: #3151b7;
    border-color: #3151b7;
  }
`

const TwoColumnContainer = styled.div`
  display: flex;
  gap: 20px;
  width: 100%;

  @media (max-width: 1024px) {
    flex-direction: column;
  }
`

const ColumnContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
  margin-bottom: 40px;
`

const ColumnHeader = styled.h3`
  text-align: center;
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 16px;
  padding: 12px;
  background-color: #f8f9fa;
  border-radius: 8px;
  color: #3151b7;
  border: 2px solid #e9ecef;
`

const FixedButtonContainer = styled.div`
  position: absolute;
  bottom: 20px;
  right: 20px;
  z-index: 10;
`

const CustomStyledButton = styled(CustomButton)`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  background-color: #1f4ab7;
  margin-top: 20px;
  margin-left: auto;
`
